import { createSarvaApiClient, SarvaApiError, type SarvaApiClient } from "@sarva/api-client";
import { readAccessToken, readPracticeId, signOut } from "./session-store";

/**
 * The one API client in the application.
 *
 * (H) doc 27 §5: all server communication goes through the generated client
 * wrapped in TanStack Query hooks — no raw `fetch` in a component, ever. This
 * module is what makes that possible: everything the transport needs that is
 * *not* in the OpenAPI document (the token, the active practice, what a `401`
 * means) is configured once, here.
 *
 * It is a module singleton rather than a React context on purpose. The client
 * holds no state — it reads the token and the practice per request through the
 * accessors in `session-store` — so a provider would add a re-render surface
 * and nothing else. Tests replace `globalThis.fetch`, which is the boundary the
 * transport actually uses.
 */

/**
 * The API origin, and it is **empty by default** — every request is
 * same-origin.
 *
 * That is the deployed shape (one origin in front of both the static bundle and
 * the API) and it is what makes doc 23 §11a's `SameSite=Strict` refresh cookie
 * reachable at all. Development gets the same shape from the dev server's
 * `/api` proxy in `vite.config.ts`, so nothing about the request differs
 * between a laptop and production.
 *
 * `VITE_API_BASE_URL` overrides it for the case the proxy cannot cover — a
 * bundle served from somewhere else entirely — and CORS then has to allow that
 * origin explicitly (doc 23 §11a: no wildcard, on any environment).
 */
export const API_BASE_URL: string = import.meta.env["VITE_API_BASE_URL"] ?? "";

/**
 * The `X-Request-Id` of the most recent response.
 *
 * doc 27 §6, the `5xx` row: the error boundary shows a request id for support.
 * A boundary catches a *render*, which happens after the response is gone, so
 * the last id is recorded here as it goes past. It is a correlation token, not
 * data — it carries no PHI (doc 22 §11).
 */
let lastRequestId: string | null = null;
export const getLastRequestId = (): string | null => lastRequestId;

/** Populated from `Retry-After` on a `429`, in seconds (doc 22 §11, §12). */
let lastRetryAfterSeconds: number | null = null;
export const getLastRetryAfterSeconds = (): number | null => lastRetryAfterSeconds;

const UNAUTHENTICATED_STATUS = 401;
const RATE_LIMITED_STATUS = 429;

function observe(response: Response): void {
  lastRequestId = response.headers.get("X-Request-Id");

  if (response.status === RATE_LIMITED_STATUS) {
    const header = response.headers.get("Retry-After");
    const seconds = header === null ? Number.NaN : Number(header);
    lastRetryAfterSeconds = Number.isFinite(seconds) ? seconds : null;
  }

  if (response.status === UNAUTHENTICATED_STATUS && readAccessToken() !== null) {
    // doc 27 §6, the `401` row: "silent token refresh; on failure,
    // re-authenticate preserving the current location".
    //
    // The refresh endpoint now exists (GAP-075), and the refresh happens in
    // `RequireAuth` rather than here — one attempt per page load, before the
    // guard decides the visitor is signed out. Retrying it inside the transport
    // would mean every in-flight request racing to refresh the same session on
    // the same `401`, which is one refresh too many and a stampede at worst.
    //
    // So this still drops straight to clearing the credential; `RequireAuth`
    // then attempts the restore and preserves the location. When OIDC lands
    // (doc 23 §2) the shape does not change — only which endpoint is called.
    //
    // The `readAccessToken` guard matters: a `401` from the sign-in call itself
    // is a wrong credential, not an expired session, and must not be reported
    // to the user as one.
    signOut("expired");
  }
}

/**
 * Reads that stay tenant-wide whichever practice is selected.
 *
 * (H) Each of these tables holds rows with **no** practice — a tenant-wide payer rule, the
 * tenant's policy band, its fee schedules — and the API's `practice_id` filter is an exact
 * match, so narrowing these reads hid every tenant-wide row. On the hosted demo the payer
 * rules screen read "No payer rules yet" beside a tenant holding nine (2026-10-13). Until
 * the API answers "this practice's rows and the tenant's" for these, they are read whole.
 */
const TENANT_WIDE_READS: readonly string[] = [
  "/api/v1/audit-log",
  "/api/v1/fee-schedule-rate-suggestions",
  "/api/v1/fee-schedules",
  "/api/v1/payer-auth-requirements",
  "/api/v1/payer-rules",
  "/api/v1/policy-bands",
  "/api/v1/report-definitions",
  "/api/v1/scrub-rules",
  "/api/v1/settings",
  "/api/v1/work-queues/health",
];

export function narrowsToPractice(path: string): boolean {
  const bare = path.startsWith(API_BASE_URL) ? path.slice(API_BASE_URL.length) : path;
  return !TENANT_WIDE_READS.some((prefix) => bare === prefix || bare.startsWith(`${prefix}?`));
}

export const api: SarvaApiClient = createSarvaApiClient({
  baseUrl: API_BASE_URL,
  getAccessToken: readAccessToken,
  getPracticeId: readPracticeId,
  narrowsToPractice,
  onResponse: observe,
});


/**
 * Downloading a file the API renders, rather than a JSON body (GAP-083).
 *
 * (H) The generated client types every `text/csv` operation as `Promise<void>` — it parses
 * JSON and there is none — so an export cannot be fetched through it. This is the seam for
 * that, and it is deliberately in `shared/api` rather than in a component: doc 27 §5's rule
 * is "no raw `fetch` in a component", and the transport is exactly where the token, the
 * practice header and the `401` handling already live.
 *
 * It goes through `observe`, so a `401` on a download signs the session out the same way a
 * `401` on any other request does — an export that silently returned an empty file when the
 * session had expired would be the worst possible answer, because the file looks plausible.
 *
 * Returns the body and the filename the server chose. Turning that into a download is the
 * caller's, because a component knows whether it is saving a file or showing a preview.
 */
export interface DownloadedFile {
  body: Blob;
  filename: string;
  /** Rows the server actually wrote, from `X-Sarva-Export-Rows`. */
  rows: number | null;
  /** True when the export hit its row cap. The file is SHORT and must be labelled as such. */
  truncated: boolean;
}

export async function downloadFile(
  path: string,
  query: Readonly<Record<string, string>> = {},
): Promise<DownloadedFile> {
  const token = readAccessToken();
  const practiceId = readPracticeId();
  // Narrowed to the active practice like every other read — see `withActivePractice` in the
  // api-client — so an export matches the screen it was started from.
  const search = new URLSearchParams(
    practiceId === null || "practice_id" in query ? query : { ...query, practice_id: practiceId },
  ).toString();
  const response = await fetch(`${API_BASE_URL}${path}${search === "" ? "" : `?${search}`}`, {
    method: "GET",
    headers: {
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      ...(practiceId === null ? {} : { "X-Sarva-Practice-Id": practiceId }),
    },
  });
  observe(response);
  if (!response.ok) {
    // The body is a problem+json document even for a route that answers CSV on success, so
    // it is thrown as one and `describeProblem` renders it like any other failure.
    throw new SarvaApiError(
      response.status,
      await safeProblem(response),
      response.headers.get("X-Request-Id"),
    );
  }
  const rows = Number(response.headers.get("X-Sarva-Export-Rows"));
  return {
    body: await response.blob(),
    filename: filenameFrom(response.headers.get("Content-Disposition")) ?? "export.csv",
    rows: Number.isFinite(rows) ? rows : null,
    truncated: response.headers.get("X-Sarva-Export-Truncated") === "true",
  };
}

async function safeProblem(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    // A refusal that is not problem+json — a proxy's own error page, say. The status is
    // still the truth and is what the caller renders.
    return { status: response.status, title: "The download was refused." };
  }
}

/**
 * The server's chosen filename.
 *
 * (H) Anything outside `[A-Za-z0-9._-]` is dropped rather than escaped. A filename is
 * written to the user's disk, and `../` in one is a path traversal in every download
 * manager that has ever had the bug. The server only ever sends `claims.csv` or
 * `denials.csv`; this is the belt, and the fallback is the braces.
 */
function filenameFrom(header: string | null): string | null {
  if (header === null) return null;
  const match = /filename="?([^";]+)"?/.exec(header);
  const raw = match?.[1];
  if (raw === undefined) return null;
  const safe = raw.replace(/[^A-Za-z0-9._-]/g, "");
  return safe === "" || safe.startsWith(".") ? null : safe;
}
